/**
 * `createWebDefaultAdapter()` — zero-config web adapter.
 *
 * Bundles the canonical web slot wiring so consumers can drop in one
 * function call instead of constructing the full `createWebPlatformAdapter`
 * dependency graph by hand:
 *
 *     import { SDKProvider } from '@morfi/backend-sdk-v2';
 *     import { createWebDefaultAdapter } from '@morfi/backend-sdk-v2/client';
 *
 *     export function Providers({ children }) {
 *       const [adapter] = useState(() => createWebDefaultAdapter());
 *       return <SDKProvider adapter={adapter}>{children}</SDKProvider>;
 *     }
 *
 * **Side effect** — also calls `installWebVisibility()` to register the
 * `IntersectionObserver`-backed `VisibilityController` on the
 * `SubjectRegistry` singleton. This MUST happen before any hook with
 * `viewportGated: true` mounts. The function is idempotent; safe to call
 * multiple times.
 *
 * **SSR contract** — returns a no-op stub adapter when `globalThis.window`
 * or `globalThis.document` is undefined (Next.js server render, jsdom-less
 * tests). The stub satisfies the full `PlatformAdapter` type so
 * `<SDKProvider>` mounts cleanly during SSR; all slots are no-ops since
 * effects don't fire on the server. On client hydration React re-runs the
 * `useState` initializer and the real adapter takes over.
 */

import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { createElement, type ReactNode } from 'react';
import type { PlatformAdapter, SyncStorageLike } from '../adapter.js';
import { createWebPlatformAdapter } from './create-platform-adapter.js';
import { installWebVisibility } from './install-visibility.js';
import { WEB_PERSISTER_KEY } from './query-persister.js';
import type { IdbKv } from './storage.js';

interface LocalStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * `IdbKv` shim backed by `globalThis.localStorage`. Avoids requiring
 * the `idb-keyval` runtime dependency at SDK level — apps that want
 * real IDB can build their own adapter via `createWebPlatformAdapter`
 * with `idb: idbKeyval` instead.
 */
function localStorageBackedIdb(): IdbKv {
  function ls(): LocalStorageLike | undefined {
    const g = globalThis as { localStorage?: LocalStorageLike };
    return g.localStorage;
  }
  return {
    async get<T = string>(key: string): Promise<T | undefined> {
      const store = ls();
      if (store === undefined) return undefined;
      const v = store.getItem(key);
      return v === null ? undefined : (v as T);
    },
    async set(key: string, value: string): Promise<void> {
      ls()?.setItem(key, value);
    },
    async del(key: string): Promise<void> {
      ls()?.removeItem(key);
    },
  };
}

function getDocumentLike(): Document | undefined {
  return (globalThis as { document?: Document }).document;
}

function getWindowLike(): Window | undefined {
  return (globalThis as { window?: Window }).window;
}

function getLocalStorage(): SyncStorageLike | undefined {
  return (globalThis as { localStorage?: SyncStorageLike }).localStorage;
}

function getNavigatorLike():
  | {
      readonly onLine: boolean;
    }
  | undefined {
  const n = (globalThis as { navigator?: { onLine: boolean } }).navigator;
  return n;
}

function ssrStubAdapter(): PlatformAdapter {
  return {
    kind: 'web',
    storage: {
      sync: {
        getItem: () => null,
        setItem: () => {},
        removeItem: () => {},
        clear: () => {},
        length: 0,
        key: () => null,
      },
      async: {
        async getItem() {
          return null;
        },
        async setItem() {},
        async removeItem() {},
      },
    },
    secureStorage: {
      supported: false,
      async getItem() {
        return null;
      },
      async setItem() {},
      async removeItem() {},
    },
    queryPersister: {
      persister: {
        persistClient() {},
        async restoreClient() {
          return undefined;
        },
        removeClient() {},
      },
      flushSync() {},
    },
    appState: {
      current: () => 'background',
      addListener: () => () => {},
    },
    netInfo: {
      current: () => ({ online: false, type: 'unknown' as const }),
      addListener: () => () => {},
    },
    visibilityRef: {
      currentRef: { current: false },
      register: () => () => {},
      observeElement: () => () => {},
    },
    screenFocus: {
      useFocusEffect: () => {},
    },
    broadcast: {
      openBroadcast: () => ({
        supported: false,
        postMessage: () => {},
        close: () => {},
        addEventListener: () => {},
      }),
    },
    // SSR: a plain `<div style={{minWidth:0}}>` matching the DOM the client's
    // `motion`-backed wrapper renders (see `row-animator.tsx`). This MUST stay
    // a real element — a children passthrough here emits one FEWER wrapper div
    // per row than the client tree, and React 19 hydration then fails
    // structurally (#418, tree regenerated) on every list that server-renders
    // rows (the anonymous /demo-mode e2e red wall, 2026-09-02). There is no
    // animation server-side either way; on hydration `motion.div` adopts the
    // plain div and the layout animator takes over.
    rowAnimator: {
      Wrapper: ({ children }: { readonly children: unknown }) =>
        createElement('div', { style: { minWidth: 0 } }, children as ReactNode),
    },
  };
}

export interface CreateWebDefaultAdapterOptions {
  /**
   * Cache namespace appended to the persister storage key, isolating each
   * value's persisted cache (e.g. one per active network). The web SDK
   * provider passes the active network so a network switch no longer restores
   * the previous network's cache into the fresh QueryClient. Omitted ⇒ the
   * shared default key.
   */
  readonly cacheNamespace?: string;
}

export function createWebDefaultAdapter(opts?: CreateWebDefaultAdapterOptions): PlatformAdapter {
  const doc = getDocumentLike();
  const win = getWindowLike();
  if (doc === undefined || win === undefined) {
    // SSR / non-browser: return a no-op adapter so consumers can mount
    // the provider unconditionally. React's hydration re-runs this and
    // upgrades to the real adapter on the client.
    return ssrStubAdapter();
  }
  installWebVisibility();
  const nav = getNavigatorLike();
  const localStorage = getLocalStorage();
  const namespace = opts?.cacheNamespace;
  const persisterKey =
    namespace !== undefined && namespace !== '' ? `${WEB_PERSISTER_KEY}:${namespace}` : undefined;
  return createWebPlatformAdapter({
    idb: localStorageBackedIdb(),
    createAsyncStoragePersister: createAsyncStoragePersister as unknown as Parameters<
      typeof createWebPlatformAdapter
    >[0]['createAsyncStoragePersister'],
    appState: { document: doc, window: win },
    netInfo: {
      window: win,
      ...(nav !== undefined ? { nav } : {}),
    },
    ...(localStorage !== undefined ? { localStorage } : {}),
    ...(persisterKey !== undefined ? { persisterKey } : {}),
  });
}
